import { jsPDF } from 'jspdf';
import { calculatePageDimensions } from './dimensions';
import { renderSpreadToPDF } from './spreadRenderer.jsx';
import { setupPDFFonts } from '../../utils/pdfFonts';
import { PAGE_SIZES } from '../../constants/pageSizes';

export async function generateHorizontalPDF(book, options = {}) {
  try {
    if (!book) {
      throw new Error('Book data is required');
    }

    if (!book.spreads) {
      throw new Error('Book spreads data is missing');
    }

    const { skipCoverSpread = false, includeBleed = true } = options;
    
    // Get the book page size and physical dimensions
    const pageSize = book.pageSize || 'us-trade';
    const physicalSize = PAGE_SIZES[pageSize] || PAGE_SIZES['us-trade'];
    
    console.log('Starting PDF generation for page size:', pageSize);
    console.log('Physical dimensions:', physicalSize);
    console.log('Including bleed area:', includeBleed ? 'Yes (0.125" on each side)' : 'No');
    
    // Calculate PDF dimensions with proper scaling and bleed area
    const dimensions = calculatePageDimensions(pageSize, { includeBleed });
    const { width, height, scale, scaleY, bleed, trimWidth, trimHeight } = dimensions;
    
    console.log('PDF dimensions:', dimensions);
    
    // Set DPI to ensure high quality printing (300 DPI is standard for print)
    const dpi = 300;
    
    console.log('Creating jsPDF instance...');
    const doc = new jsPDF({
      unit: 'pt',
      format: [width * 2, height], // Double width for a spread
      orientation: 'landscape',
      putOnlyUsedFonts: true,
      floatPrecision: 16,
      hotfixes: ['px_scaling'],   // Enable better pixel scaling
    });
    
    // Set metadata for the PDF
    doc.setProperties({
      title: `${book.title || 'Book'} - Print Version`,
      author: book.author || 'StorybookWizBolt',
      subject: 'Print-ready book PDF',
      keywords: 'book, print, lulu',
      creator: 'StorybookWizBolt'  
    });

    // Set up fonts first
    console.log('Setting up fonts...');
    const fontSetupResult = await setupPDFFonts(doc);
    console.log('Font setup complete, result:', fontSetupResult);
    console.log('Available fonts:', doc.getFontList());

    // Cover spread - only include if not skipping
    let isFirstPage = true;
    if (!skipCoverSpread && book.spreads['spread-0']) {
      const coverSpread = {
        ...book.spreads['spread-0'],
        frontCoverColor:
          book.coverDetails?.frontCover?.color ||
          book?.frontCoverColor ||
          '#FFFFFF',
      };
      await renderSpreadToPDF(doc, coverSpread, { 
        scale, 
        scaleY, 
        isCoverSpread: true, 
        bleed, 
        includeBleed,
        omitImages: options.omitImages
      });
      isFirstPage = false;
    }
    
    // Add a title page spread after the cover (or as first page if cover is skipped)
    if (!isFirstPage) {
      doc.addPage();
    }
    
    // Helper functions for color handling
    function hexToRgb(hex) {
      hex = hex.replace(/^#/, '');
      const r = parseInt(hex.substring(0, 2), 16) || 0;
      const g = parseInt(hex.substring(2, 4), 16) || 0;
      const b = parseInt(hex.substring(4, 6), 16) || 0;
      return { r, g, b };
    }
    
    function rgbToHex(r, g, b) {
      return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    }
    
    // Set up the title page spread - with title on the right page only
    console.log('📄 Adding title page spread in horizontal PDF (right side only)');
    
    // Get the book title and author
    const bookTitle = book.title || 'Untitled Book';
    const bookAuthor = book.author || '';
    
    // Calculate dimensions for the spread - total width and page width
    const spreadWidth = doc.internal.pageSize.getWidth();
    const pageWidth = spreadWidth / 2;
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Fill the entire spread with white
    doc.setFillColor('#FFFFFF');
    doc.rect(
      0,
      0,
      spreadWidth,
      pageHeight,
      'F'
    );
    
    // Set font for the title
    doc.setFont('Baskervville-Regular', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Calculate the center of the RIGHT page
    const rightPageCenterX = pageWidth + (pageWidth / 2);
    
    // Handle long titles - calculate appropriate font size and wrap if needed
    let titleFontSize = 48; // Default font size for horizontal PDF
    const maxWidth = pageWidth * 0.8; // Maximum width (80% of right page width)
    
    // Calculate title width to check if we need to adjust
    doc.setFontSize(titleFontSize);
    const titleWidth = doc.getTextWidth(bookTitle);
    
    // If title is too wide, reduce font size or add wrapping
    if (titleWidth > maxWidth) {
      // First approach: reduce font size if it's just slightly too wide
      if (titleWidth < maxWidth * 1.5) {
        // Adjust font size to fit
        titleFontSize = Math.max(28, titleFontSize * (maxWidth / titleWidth));
        doc.setFontSize(titleFontSize);
      } else {
        // For very long titles, keep reasonable font size but wrap text
        titleFontSize = 36; // Smaller but still prominent
        doc.setFontSize(titleFontSize);
      }
    } else {
      doc.setFontSize(titleFontSize); // Set default font size
    }
    
    // Render the title centered on the RIGHT page with appropriate wrapping
    doc.text(
      bookTitle,
      rightPageCenterX,
      pageHeight / 4,  // Position at 1/4 from the top
      { 
        align: 'center',
        maxWidth: maxWidth
      }
    );
    
    // Get the height of the title text (including wrapping if any)
    const titleDims = typeof doc.getTextDimensions === 'function'
      ? doc.getTextDimensions(bookTitle, { maxWidth })
      : { h: titleFontSize * 1.2 };
    const titleTextHeight = titleDims.h;
    
    // Add author if available - on the RIGHT page
    if (bookAuthor) {
      doc.setFontSize(28);
      doc.setTextColor(60, 60, 60);
      doc.text(
        'By ' + bookAuthor,
        rightPageCenterX,
        pageHeight / 4 + titleTextHeight + 40,  // 40pts below the title
        { align: 'center' }
      );
    }
    
    // Add attribution on two lines at the bottom of the right page
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128); // Medium gray color
    // Use a single text element with newline character
    doc.text(
      'Created With\nStorybook Wizard',
      rightPageCenterX,
      pageHeight - 60, // Slightly higher from the bottom
      { 
        align: 'center',
        lineHeightFactor: 1.5 // Adjust line spacing
      }
    );
    
    // Add a debug label if in debug mode
    if (options.debug) {
      doc.setFontSize(8);
      doc.setTextColor(255, 0, 0);
      doc.text('Left Page (Blank)', pageWidth * 0.5, 20, { align: 'center' });
      doc.text('Right Page (Title Page)', pageWidth * 1.5, 20, { align: 'center' });
    }
    
    // Mark that we've rendered a page
    isFirstPage = false;

    // Content spreads
    const totalSpreads = Math.ceil(book.totalPages / 2);
    const pageStart = Math.max(1, parseInt(options.pageStart || 1, 10));
    const pageEnd = Math.min(parseInt(book.totalPages, 10), parseInt(options.pageEnd || book.totalPages, 10));
    const spreadStart = Math.max(1, Math.ceil(pageStart / 2));
    const spreadEnd = Math.min(totalSpreads, Math.ceil(pageEnd / 2));
    const totalToRenderSpreads = spreadEnd - spreadStart + 1;
    for (let spreadNumber = spreadStart; spreadNumber <= spreadEnd; spreadNumber++) {
      // Yield to event loop to avoid long tasks freezing the UI
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!isFirstPage) {
        doc.addPage();
      }
      isFirstPage = false;
      
      const spreadId = `spread-${spreadNumber}`;
      const spread = book.spreads[spreadId];

      // Skip if spread is undefined, but log it for debugging
      if (!spread) {
        console.warn(`Spread ${spreadId} is undefined, skipping...`);
        continue;
      }

      // Ensure spread has required properties
      const validSpread = {
        ...spread,
        elements: spread.elements || [],
      };

      // Make sure to pass both scale, scaleY and bleed parameters to content spreads
      await renderSpreadToPDF(doc, validSpread, { 
        scale, 
        scaleY, 
        bleed, 
        includeBleed,
        hasPageNumbers: book.hasPageNumbers,
        spreadNumber,
        width,
        height,
        omitImages: options.omitImages
      });
      
      // Log spread contents for debugging
      console.log(`Rendering spread ${spreadId} with ${validSpread.elements?.length || 0} elements:`, 
        validSpread.elements?.map(e => ({ 
          type: e.type, 
          id: e.id?.substring(0,8),
          hasImage: e.type === 'image' ? (!!e.src) : 'n/a'
        })))

      // Progress callback
      try {
        if (typeof options.onProgress === 'function') {
          const current = spreadNumber - spreadStart + 1;
          options.onProgress({ type: 'horizontal', current, total: totalToRenderSpreads, spreadNumber });
        }
      } catch (_) {}
    }

    return doc.output('blob');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
}
